export const EMPTY_DEPENDENT = {
  name: "",
  relationship: "",
  dob: "",
  gender: "",
  contactNumber: "",
};

export const EMPTY_ADDITIONAL_PROFILE = {
  employeeCode: "",
  gender: "",
  dob: "",
  maritalStatus: "",
  fatherOrHusbandName: "",
  cnic: "",
  phoneNumber: "",
  emergencyContactNumber: "",
  emergencyContactName: "",
  bloodGroup: "",
  passportNumber: "",
  ntn: "",
  disability: null,
  disabilityDescription: "",
  postalCode: "",
  city: "",
  countryId: "",
  permanentAddress: "",
  residentialAddress: "",
  dependents: [],
};

export function resolveCountryIdFromProfile(profile, countries = []) {
  if (!profile) return "";
  const direct = profile.countryId ?? profile.CountryId;
  if (direct !== undefined && direct !== null && direct !== "") {
    return Number(direct);
  }
  const legacy = profile.country ?? profile.Country;
  if (!legacy || !Array.isArray(countries) || countries.length === 0) return "";
  const legacyStr = String(legacy).trim().toLowerCase();
  const match = countries.find(
    (c) =>
      String(c.text || "")
        .trim()
        .toLowerCase() === legacyStr || String(c.value) === String(legacy)
  );
  return match?.value ?? "";
}

export function resolveTeamIdsFromUser(user) {
  if (!user) return [];
  if (Array.isArray(user.teamIds) && user.teamIds.length > 0) {
    return user.teamIds.map(String);
  }
  if (Array.isArray(user.teams) && user.teams.length > 0) {
    return user.teams.map((t) => String(t.teamId ?? t.TeamId ?? t.id ?? t.Id)).filter(Boolean);
  }
  const legacy = user.teamId ?? user.TeamId;
  return legacy ? [String(legacy)] : [];
}

function formatDobForInput(dob) {
  if (!dob || dob === "0001-01-01T00:00:00" || dob === "0001-01-01") return "";
  if (typeof dob === "string" && /^\d{4}-\d{2}-\d{2}/.test(dob)) {
    return dob.slice(0, 10);
  }
  try {
    const d = new Date(dob);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  } catch {
    return "";
  }
}

function resolveDependentsFromProfile(profile) {
  const dependents = profile.dependents ?? profile.Dependents;
  if (!Array.isArray(dependents) || dependents.length === 0) return [];
  return dependents.map((dep) => ({
    name: dep.name ?? dep.Name ?? "",
    relationship: dep.relationship ?? dep.Relationship ?? "",
    dob: formatDobForInput(dep.dob ?? dep.Dob ?? dep.DOB),
    gender: dep.gender ?? dep.Gender ?? "",
    contactNumber: dep.contactNumber ?? dep.ContactNumber ?? "",
  }));
}

export function resolveAdditionalProfileFromUser(user, countries = []) {
  const profile = user?.additionalProfile ?? user?.AdditionalProfile ?? {};
  return {
    ...EMPTY_ADDITIONAL_PROFILE,
    employeeCode: profile.employeeCode ?? profile.EmployeeCode ?? "",
    gender: profile.gender ?? profile.Gender ?? "",
    dob: formatDobForInput(profile.dob ?? profile.Dob ?? profile.DOB),
    maritalStatus: profile.maritalStatus ?? profile.MaritalStatus ?? "",
    fatherOrHusbandName: profile.fatherOrHusbandName ?? profile.FatherOrHusbandName ?? "",
    cnic: profile.cnic ?? profile.Cnic ?? profile.CNIC ?? "",
    phoneNumber: profile.phoneNumber ?? profile.PhoneNumber ?? "",
    emergencyContactNumber: profile.emergencyContactNumber ?? profile.EmergencyContactNumber ?? "",
    emergencyContactName: profile.emergencyContactName ?? profile.EmergencyContactName ?? "",
    bloodGroup: profile.bloodGroup ?? profile.BloodGroup ?? "",
    passportNumber: profile.passportNumber ?? profile.PassportNumber ?? "",
    ntn: profile.ntn ?? profile.Ntn ?? profile.NTN ?? "",
    disability:
      profile.disability !== undefined && profile.disability !== null
        ? profile.disability
        : profile.Disability !== undefined && profile.Disability !== null
          ? profile.Disability
          : null,
    disabilityDescription: profile.disabilityDescription ?? profile.DisabilityDescription ?? "",
    postalCode: profile.postalCode ?? profile.PostalCode ?? "",
    city: profile.city ?? profile.City ?? "",
    countryId: resolveCountryIdFromProfile(profile, countries),
    permanentAddress: profile.permanentAddress ?? profile.PermanentAddress ?? "",
    residentialAddress: profile.residentialAddress ?? profile.ResidentialAddress ?? "",
    dependents: resolveDependentsFromProfile(profile),
  };
}

export function buildAdditionalProfilePayload(profile) {
  if (!profile || typeof profile !== "object") return null;

  const payload = {};
  const stringFields = [
    "gender",
    "maritalStatus",
    "fatherOrHusbandName",
    "cnic",
    "phoneNumber",
    "emergencyContactNumber",
    "emergencyContactName",
    "bloodGroup",
    "passportNumber",
    "ntn",
    "disabilityDescription",
    "postalCode",
    "city",
    "permanentAddress",
    "residentialAddress",
  ];

  stringFields.forEach((key) => {
    const value = profile[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      payload[key] = String(value).trim();
    }
  });
  payload.employeeCode = String(profile.employeeCode ?? "").trim();

  if (profile.dob) {
    payload.dob = profile.dob;
  }

  if (profile.disability === true || profile.disability === false) {
    payload.disability = profile.disability;
  }

  if (
    profile.countryId !== undefined &&
    profile.countryId !== null &&
    profile.countryId !== ""
  ) {
    const countryId = Number(profile.countryId);
    if (!Number.isNaN(countryId) && countryId > 0) {
      payload.countryId = countryId;
    }
  }

  // Always send the dependents array so the backend replaces the full list.
  // dob must be null (not "") — the API binds it to a nullable DateTime.
  payload.dependents = (Array.isArray(profile.dependents) ? profile.dependents : [])
    .filter((dep) => dep && dep.name && String(dep.name).trim() !== "")
    .map((dep) => ({
      name: String(dep.name).trim(),
      relationship: dep.relationship ? String(dep.relationship).trim() : "",
      dob: dep.dob || null,
      gender: dep.gender ? String(dep.gender).trim() : "",
      contactNumber: dep.contactNumber ? String(dep.contactNumber).trim() : "",
    }));

  return Object.keys(payload).length > 0 ? payload : null;
}

export function buildTeamIdsPayload(teamIds) {
  if (!Array.isArray(teamIds)) return [];
  return teamIds.map(String).filter(Boolean);
}

/** Gender options for user form (max 10 chars per API). Display labels via i18n. */
export function getUserFormGenderOptions(t) {
  return [
    { value: "Male", label: t("app.users.genderMale") },
    { value: "Female", label: t("app.users.genderFemale") },
    { value: "Non-binary", label: t("app.users.genderNonBinary") },
    { value: "Other", label: t("app.users.genderOther") },
    { value: "Not stated", label: t("app.users.genderPreferNotToSay") },
  ];
}

export const USER_FORM_GENDER_VALUES = ["Male", "Female", "Non-binary", "Other", "Not stated"];

/** Parse YYYY-MM-DD from date input without timezone shift. */
export function parseLocalDateInput(value) {
  if (value === undefined || value === null || String(value).trim() === "") return null;
  const match = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

/** Today's date as YYYY-MM-DD in local timezone (for date input max). */
export function getLocalDateInputMax() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function compareLocalDateParts(a, b) {
  if (a.year !== b.year) return a.year - b.year;
  if (a.month !== b.month) return a.month - b.month;
  return a.day - b.day;
}

/** True when empty or on/before today (local calendar date). */
export function isDateOfBirthNotFuture(value) {
  const parsed = parseLocalDateInput(value);
  if (!parsed) return value === undefined || value === null || String(value).trim() === "";
  const now = new Date();
  const today = {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    day: now.getDate(),
  };
  return compareLocalDateParts(parsed, today) <= 0;
}
